const BigNumber = require('bignumber.js');
const CustomerScreenStages = require('@/lib/CustomerScreen/CustomerScreenStages');
const EventNames = require('@/lib/Events/EventNames');
const Payment = require('@/lib/Payments/Payment');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PaymentToken = require('@/lib/Payments/PaymentToken');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const VerifoneActionNames = require('@/lib/PaymentMethods/Verifone/Constants/VerifoneActionNames');
const VerifoneIntegrationType = require('@/lib/PaymentMethods/Verifone/VerifoneIntegrationType');
const VerifoneStatusNames = require('@/lib/PaymentMethods/Verifone/Constants/VerifoneStatusNames');
const VerifoneStatusRecord = require('@/lib/PaymentMethods/Verifone/Records/Response/VerifoneStatusRecord');
const VerifoneTransactionModifiers = require('@/lib/PaymentMethods/Verifone/Constants/VerifoneTransactionModifiers');
const VerifoneTransactionRequestRecord = require('@/lib/PaymentMethods/Verifone/Records/Request/VerifoneTransactionRequestRecord');
const VerifoneTransactionResponseRecord = require('@/lib/PaymentMethods/Verifone/Records/Response/VerifoneTransactionResponseRecord');
const { formatCurrency, formatMoney } = require('@/lib/Formatting/currency');
const { hydrate } = require('@/lib/Hydration');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');
const { ModalCancelledError } = require('@/vue/modals/ModalCancelledError');
const { suppressModalCancelledErrorOrReThrow } = require('@/lib/Errors');
const { toTitleCase } = require('@/functions/strings');
const { updateAngularUi } = require('@/functions/angular');

/**
 * This is the controller for the inner part of the payment modal for the payment method of "Verifone".
 *
 * @param $filter
 * @param $modal
 * @param $scope
 * @param $timeout
 * @param {ApiClient} apiClient
 * @param {CartService} cartService
 * @param {CurrencyService} currencyService
 * @param {CustomerScreenDataService} customerScreenDataService
 * @param {DialogService} dialogService
 * @param {LangService} langService
 * @param {PaymentMethodService} paymentMethodService
 * @param {PaymentState} paymentState
 * @param {ToastFactory} toastFactory
 * @param {UserService} userService
 * @param {VerifoneConfig} verifoneConfig
 * @param {VerifoneDriver} verifoneDriver
 * @param {VueModalFactory} vueModalFactory
 */
const VerifonePaymentCtrl = function VerifonePaymentCtrl(
    $filter,
    $modal,
    $scope,
    $timeout,
    apiClient,
    cartService,
    currencyService,
    customerScreenDataService,
    dialogService,
    langService,
    paymentMethodService,
    paymentState,
    toastFactory,
    userService,
    verifoneConfig,
    verifoneDriver,
    vueModalFactory
) {
    $scope.paymentMethodScopes[PaymentMethodRef.CARD_VERIFONE_CHIP_AND_PIN] = $scope;

    $scope.cart = cartService;
    $scope.paymentState = paymentState;
    $scope.verifoneConfig = verifoneConfig;
    $scope.verifoneDriver = verifoneDriver;

    // Set standard payment controller things.
    $scope.thisPaymentMethodRef = PaymentMethodRef.CARD_VERIFONE_CHIP_AND_PIN;
    $scope.thisPaymentMethod = paymentMethodService.getByRef($scope.thisPaymentMethodRef);

    // Make some constants available in the template.
    $scope.TransactionModifiers = VerifoneTransactionModifiers;

    // Watch the VerifoneDriver's internal state object and update the PaymentState object that controls whether
    // the Payment modal can be closed.
    $scope.$watch('verifoneDriver.state.canCancel', function (value) {
        // Even if the transaction is cancelable from within the VerifoneDriver we want to make sure it not
        // cancelable by just clicking 'Cancel' on the payment modal if it is in these states. If the operator
        // wants to cancel the transaction once the terminal has been woken up they need to either use the
        // 'Cancel Transaction' button or cancel via the terminal itself.
        const preventCancelStatuses = [
            VerifoneStatusNames.AWAITING_CARD,
            VerifoneStatusNames.STARTING_TRANSACTION,
        ];

        let cancelable = !$scope.transactionInProgress;

        if (value === false) {
            cancelable = false;
        }

        if (
            cancelable
            && verifoneDriver.state.lastProgressRecordReceived
            && preventCancelStatuses.indexOf(verifoneDriver.state.lastProgressRecordReceived.statusName) !== -1
        ) {
            cancelable = false;
        }

        paymentState.setPaymentModalCancelable(
            cancelable,
            $scope.thisPaymentMethodRef
        );
    });

    /**
     * The last status record received is remembered because it is used
     * to determine which action buttons should be available to the user.
     *
     * @type {VerifoneStatusRecord}
     */
    $scope.lastStatusRecord = null;

    /**
     * The messages received from the terminal can generally be classified into 3 types.
     * Instead of showing a big list of all the messages received on the screen (which is information overload
     * for the user), the latest one of each of the 3 types is shown.
     *
     * - The overall status of the transaction (success, failed)
     * - What the terminal is doing now (printing, waiting for card, etc..)
     * - What the user should do now (continue, voice referral, etc)
     *
     * @type {object|null}
     */
    $scope.lastTransactionStatusInfo = null;
    /**
     * @type {object|null}
     */
    $scope.lastTerminalStatusInfo = null;

    /**
     * A full history of status messages displayed, for debugging purposes.
     *
     * @type {object[]}
     */
    $scope.statusInfoHistory = [];

    /**
     * Are we doing a special type of transaction?
     * Auth only
     * CNP
     * CNP - Account On File
     *
     * @type {null}
     */
    $scope.transactionModifier = null;

    /**
     * If charging an existing saved card, this will be the tokenId of that card.
     *
     * @type {PaymentToken|null}
     */
    $scope.accountOnFileToken = null;

    /**
     * Are we doing a refund transaction?
     *
     * @type {boolean}
     */
    $scope.isRefund = false;

    /**
     * This 'ready' flag is switched to true only once per the controller being loaded, when the payment
     * terminal is ready. This will be false while updates, etc. are happening so a message can be displayed.
     *
     * @type {boolean}
     */
    $scope.initialReady = false;

    $scope.transactionAmount = null;
    $scope.transactionInProgress = false;
    $scope.tryAgainIsVisible = false;

    $scope.defaultWaitingIcon = $scope.verifoneConfig.posClient ? 'arrows-rotate fa-spin' : 'circle-info';

    /**
     * Reference of last transaction sent to Verifone.
     *
     * @type {string|null}
     */
    $scope.reference = null;

    /**
     * A function to call when the ready status is received.
     *
     * @type {function|null}
     */
    $scope.onReady = null;

    /**
     * Register listener to receive records coming from the terminal.
     */
    $scope.verifoneDriver.clearRecordReceivedListeners();
    $scope.verifoneDriver.onRecordReceived(
        function (record) {
            if (record instanceof VerifoneStatusRecord) {
                $scope.handleReceivedStatusRecord(record);
            } else if (record instanceof VerifoneTransactionResponseRecord) {
                $scope.handleReceivedTransactionResponseRecord(record);
            } else {
                $scope.handleGenericReceivedRecord(record);
            }
        }
    );

    const setTransactionInProgress = (value) => {
        $scope.transactionInProgress = value;
    };

    /**
     * @param {VerifoneStatusRecord} record
     */
    $scope.handleReceivedStatusRecord = function handleReceivedStatusRecord(record) {
        /**
         * On both POS Client and Engage you receive a transaction response and a status record when a voice
         * referral is required (merchant needs to call a number).
         * On Engage terminals (P400 / E355) the transaction record does not contain the phone number.
         * On POS Client the status record does not contain the amount.
         * So we can't just use one way for both - we need to use a different data source depending on which type.
         * For Engage terminals trigger voice referral from the status record...
         */
        if (verifoneDriver.config.integrationType === VerifoneIntegrationType.ENGAGE
            && record.statusName === VerifoneStatusNames.VOICE_REFERRAL) {
            handleVoiceReferralFromStatusRecord(record);
        }

        if (record.statusName === VerifoneStatusNames.READY) {
            // If this is the ready notification fire the onReady listener then unset it so it does not fire again.
            setTransactionInProgress(false);
            if (typeof $scope.onReady === 'function') {
                $scope.onReady();
                $scope.onReady = null;
            }
        }

        // Don't display these status messages.
        let ignoreStatuses = [
            VerifoneStatusNames.CARD_DATA_RETRIEVAL,
            // VerifoneStatusNames.PROCESSING_TRANSACTION,
        ];
        if (ignoreStatuses.indexOf(record.statusName) !== -1) {
            return;
        }

        // Set the lastStatusRecord so the appropriate action buttons are shown.
        $scope.lastStatusRecord = record;

        // Update the actions but not the displayed message for these status messages.
        let hideStatuses = [
            VerifoneStatusNames.CONTINUE_REQUIRED,
        ];
        if (hideStatuses.indexOf(record.statusName) !== -1) {
            return;
        }

        // Update message displayed to user.
        let userFacingMessage = $scope.getStatusRecordDisplayMessage(record);
        let userFacingIcon = record.icon || $scope.defaultWaitingIcon;

        $scope.addStatusInfo(userFacingMessage, 'info', userFacingIcon, false, false);

        // Update message displayed on customer facing screen.
        if (record.customerFacingMessage) {
            $scope.setCustomerFacingStatus(record.customerFacingMessage);
        }
    };

    /**
     * @param {VerifoneTransactionResponseRecord} record
     */
    $scope.handleReceivedTransactionResponseRecord = function handleReceivedTransactionResponseRecord(record) {
        // Message displayed to user.
        let userFacingMessage = record.message;
        let userFacingTextType = 'info';

        /**
         * Result types:
         *
         * 0 – Completed
         * 2 – Referred
         * 5 – Declined
         * 6 – Authorised
         * 7 – Reversed
         * 8 – Comms Down
         * 100 - Status Message
         * -nn – Negative values are used to define error conditions
         */
        if (record.result === 0) {
            // Completed.

            // Check that transactionValue (before cashback, gratuities, etc.) is the one we requested.
            // This is an extra check in case of errors in ProPoint where for some reason (probably a bug) the
            // operator was able to close the payment modal or switch payment method while a transaction was in
            // progress. This is subtly different from simply reloading ProPoint while a transaction is in progress
            // which will result in an unattached payment but which is easy to attach using the 'Manual Reconciliation'
            // button.

            // Note: there is no way of distinguishing between a debit (payment) and credit (refund) in the returned
            // record. It will return a positive number as the transactionValue for both.

            // If the transactionAmount !== expectedAmount we are probably recovering from an error
            // we can't reliably tell from the state of ProPoint either, so we won't show this to the operator.
            // We tried this, and it
            // just seemed to cause confusion so just prompt them to reprint and check the card receipts.
            let transactionAmount = record.transactionValue;
            let expectedAmount = $scope.transactionAmount;

            if (Math.abs(transactionAmount) === Math.abs(expectedAmount)) {
                let payment = record.toPayment($scope.isRefund);
                payment.setPaymentMethod($scope.thisPaymentMethod);
                payment.setThirdPartyID($scope.reference);

                if ($scope.accountOnFileToken) {
                    payment.setPaymentTokenID($scope.accountOnFileToken.ID);
                }

                $scope.addPaymentAndAutoFinish(payment);

                // The Verifone accreditation script suggests we need to say clearly if it is a contactless transaction.
                let successMessage = toTitleCase(record.message);
                if (record.captureMethod && record.captureMethod.indexOf('Contactless') === 0 && successMessage.indexOf('Contactless') === -1) {
                    successMessage = 'Contactless ' + successMessage;
                }
                paymentState.setSuccessMessage(successMessage);

                // Allow another payment for the case of a partial payment.
                setTransactionInProgress(false);
                $scope.tryAgainIsVisible = true;
                $scope.accountOnFileToken = null;
            } else {
                // We can't deal with this so let the operator know. However, we only need to show this for actual
                // transactions not when reprinting a receipt for example.

                if (record.transactionValue !== 0) {
                    toastFactory.errorTop(
                        'PAYMENT.VERIFONE_CHECK_PAYMENT_TITLE',
                        langService.getText(
                            'PAYMENT.VERIFONE_CHECK_PAYMENT_ERROR',
                        )
                    );
                }
            }
            userFacingTextType = 'success';
        } else if (record.result === 2
            && verifoneDriver.config.integrationType === VerifoneIntegrationType.POS_CLIENT) {
            /**
             * On both POS Client and Engage you receive a transaction response and a status record when a voice
             * referral is required (merchant needs to call a number).
             * On Engage terminals (P400 / E355) the transaction record does not contain the phone number.
             * On POS Client the status record does not contain the amount.
             * So we can't just use one way for both - we need to use a different data source depending on which type.
             * For POS Client trigger voice referral from the transaction response record...
             */
            handleVoiceReferralFromTransactionRecord(record);
        } else if (!!$scope.transactionModifier && record.result === 6) {
            // Received after a CNP transaction returns it's address / CSC validation result.
            // The user needs to decide if they wish to continue based on the result.
            $scope.displayAvsResult(record);
            return;
        } else if (record.result === 7 || record.result < 0) {
            // Declined (7) or any other error (< 0)
            setTransactionInProgress(false);
            $scope.tryAgainIsVisible = true;
            userFacingTextType = 'danger';

            if (record.result === -85) {
                // Transaction attempted while terminal isn't logged in.
                // Attempt to login to the terminal then try again.
                $scope.onReady = () => {
                    $scope.tryAgain();
                };
                verifoneDriver.login();
            }
        }

        $scope.addStatusInfo(userFacingMessage, userFacingTextType, null, true, false);
    };

    /**
     * @param record
     */
    $scope.handleGenericReceivedRecord = function handleGenericReceivedRecord(record) {
        // Message displayed to user.
        let userFacingMessage = record.message;
        let userFacingTextType = 'info';

        if (record.result < 0) {
            // Some other type of record with an error (< 0)
            setTransactionInProgress(false);
            $scope.tryAgainIsVisible = true;
            userFacingTextType = 'danger';
        } else if (record.result === 0 || record.result === 6) {
            // Some other type of record with success.
            userFacingTextType = 'success';
        }

        $scope.addStatusInfo(userFacingMessage, userFacingTextType, null, false, false);
    };

    /**
     * @param {VerifoneTransactionResponseRecord} responseRecord
     */
    $scope.displayAvsResult = function displayAvsResult(responseRecord) {
        let html = langService.getText('PAYMENT.AVS_HOUSE_RESULT_LABEL') + ': '
            + langService.getText('VERIFONE.AVS_RESULT_' + responseRecord.avsHouseNumberResult)
            + '<br/>' + langService.getText('PAYMENT.AVS_POSTCODE_RESULT_LABEL') + ': '
            + langService.getText('VERIFONE.AVS_RESULT_' + responseRecord.avsPostCodeResult)
            + '<br/>' + langService.getText('PAYMENT.CSC_RESULT_LABEL') + ': '
            + langService.getText('VERIFONE.AVS_RESULT_' + responseRecord.cscResult);

        $scope.addStatusInfo(html, 'info', false, true, true);
    };

    /**
     * @param {VerifoneStatusRecord} record
     */
    const handleVoiceReferralFromStatusRecord = (record) => {
        const tel = record.parameters.VRT || record.parameters['VR TEL NO'];
        const amount = record.parameters.AMOUNT || $scope.transactionAmount;
        const mid = record.parameters.MID;
        const tid = record.parameters.TID;
        const message = '<span class="voice-referral-info">' + langService.getText('PAYMENT.VOICE_REFERRAL_DESC')
            + '<span><strong>' + langService.getText('PAYMENT.VOICE_REFERRAL_TEL_LABEL') + ':</strong> ' + tel + '</span>'
            + '<span><strong>' + langService.getText('PAYMENT.VOICE_REFERRAL_AMOUNT_LABEL') + ':</strong> ' + $filter('currencySymbol')(amount) + '</span>'
            + '<span><strong>' + langService.getText('PAYMENT.VOICE_REFERRAL_MID_LABEL') + ':</strong> ' + mid + '</span>'
            + '<span><strong>' + langService.getText('PAYMENT.VOICE_REFERRAL_TID_LABEL') + ':</strong> ' + tid + '</span>';

        displayVoiceReferral(message);
    };

    /**
     * @param {VerifoneTransactionResponseRecord} record
     */
    const handleVoiceReferralFromTransactionRecord = (record) => {
        const message = '<span class="voice-referral-info">' + langService.getText('PAYMENT.VOICE_REFERRAL_DESC')
            + '<span><strong>' + langService.getText('PAYMENT.VOICE_REFERRAL_TEL_LABEL') + ':</strong> ' + record.referralTelephoneNumber + '</span>'
            + '<span><strong>' + langService.getText('PAYMENT.VOICE_REFERRAL_AMOUNT_LABEL') + ':</strong> ' + $filter('currencySymbol')(record.totalValue) + '</span>'
            + '<span><strong>' + langService.getText('PAYMENT.VOICE_REFERRAL_MID_LABEL') + ':</strong> ' + record.merchantNumber + '</span>'
            + '<span><strong>' + langService.getText('PAYMENT.VOICE_REFERRAL_TID_LABEL') + ':</strong> ' + record.terminalId + '</span>';

        displayVoiceReferral(message);
    };

    const displayVoiceReferral = (message) => {
        dialogService.prompt(
            '',
            {
                customHtml: message,
                okLabel: 'PAYMENT.VOICE_REFERRAL_AUTHORISE_BTN',
                okColor: 'success',
                cancelLabel: 'PAYMENT.VOICE_REFERRAL_REJECT_BTN',
                cancelColor: 'danger',
                inputLabel: 'PAYMENT.VOICE_REFERRAL_INPUT_LABEL',
                maxLength: 9,
                minLength: 2,
                title: 'PAYMENT.VOICE_REFERRAL',
                onKeyUp: function ($dialogScope) {
                    if ($dialogScope.form.value) {
                        // Only letters and numbers in uppercase.
                        $dialogScope.form.value = $dialogScope.form.value.replace(/[^a-z0-9]/gi, '').toUpperCase().trim();
                    } else {
                        $dialogScope.form.value = '';
                    }
                },
            }
        )
            .then((result) => {
                if (result.value) {
                    const actionRecord = verifoneDriver.createActionRecord(
                        VerifoneActionNames.VOICE_REFERRAL_AUTHORISED
                    );
                    actionRecord.parameters.AUTHCODE = result.value;
                    $scope.verifoneDriver.sendRecord(actionRecord);
                }
            })
            .catch((err) => {
                if (err instanceof ModalCancelledError) {
                    let actionRecord = verifoneDriver.createActionRecord(VerifoneActionNames.VOICE_REFERRAL_REJECTED);
                    $scope.verifoneDriver.sendRecord(actionRecord);
                } else {
                    throw err;
                }
            })
            .finally(() => {
                // After submitting the voice referral dialog for Engage there is a 1-2 second delay before the terminal
                // responds with the next status. During that time the user would see the 2 action buttons and probably
                // try and press them. So remove them.
                if ($scope.lastStatusRecord) {
                    $scope.lastStatusRecord.possibleActionRecords = [];
                }

                updateAngularUi();
            });
    };

    $scope.makePartialPayment = function makePartialPayment() {
        $scope.cancel();
        $scope.showPartialPaymentModal('make', function (amount) {
            if (amount) {
                $scope.beginTransaction(amount);
            }
        });
    };

    $scope.cancel = function cancel() {
        if ($scope.verifoneDriver.state.canCancel === true) {
            $scope.addStatusInfo('Cancelling Transaction', 'info', $scope.defaultWaitingIcon, false);
            $scope.verifoneDriver.cancelTransaction();
            $scope.lastStatusRecord = null; // Hide the action buttons.
            setTransactionInProgress(false);
        }
    };

    /**
     * @param {VerifoneContinueTransactionRecord} record
     */
    $scope.sendActionRecord = function sendActionRecord(record) {
        switch (record.actionName) {
            case VerifoneActionNames.CASHBACK_REQUIRED:
                $scope.enterCashback(
                    function (value) {
                        value = parseFloat(value);
                        $scope.cashbackAmount = value;

                        if (value) {
                            record.parameters.CASHBACK = (new BigNumber(value)).toFixed(2);
                            $scope.verifoneDriver.sendRecord(record);
                            return;
                        }

                        $scope.verifoneDriver.sendRecord(
                            $scope.verifoneDriver.createActionRecord(VerifoneActionNames.CASHBACK_NOT_REQUIRED)
                        );
                    }
                );
                break;

            case VerifoneActionNames.CANCEL_TRANSACTION:
                $scope.addStatusInfo('Cancelling Transaction', 'info', $scope.defaultWaitingIcon, false);
                // Fallthrough on purpose... (This comment makes PHPStorm not highlight this as a warning.)

            default:
                $scope.verifoneDriver.sendRecord(record);
        }
    };

    $scope.toggleAuthOnly = function toggleAuthOnly() {
        if ($scope.transactionModifier === VerifoneTransactionModifiers.AUTHORISATION_ONLY) {
            $scope.transactionModifier = null;
            $scope.tryAgain();
            return;
        }

        $scope.privilegesManager.requirePrivilege(PrivilegeNames.AUTH_ONLY_PAYMENTS).then(
            /**
             * @param {PrivilegeCheckResult} result
             */
            function (result) {
                if (result.granted) {
                    $scope.transactionModifier = VerifoneTransactionModifiers.AUTHORISATION_ONLY;
                    $scope.tryAgain();
                }
            }
        );
    };

    $scope.toggleCnpTelephone = function toggleCnpTelephone() {
        if ($scope.transactionModifier === VerifoneTransactionModifiers.CNP_TELEPHONE_ORDER) {
            $scope.transactionModifier = null;
            $scope.tryAgain();
            return;
        }

        $scope.privilegesManager.requirePrivilege(PrivilegeNames.CNP_PAYMENTS).then(
            /**
             * @param {PrivilegeCheckResult} result
             */
            function (result) {
                if (result.granted) {
                    $scope.transactionModifier = VerifoneTransactionModifiers.CNP_TELEPHONE_ORDER;
                    $scope.tryAgain();
                }
            }
        );
    };

    /**
     * Payment tokens from the currently selected customer account,
     * filtered to only those for the Verifone payment method.
     *
     * @type {PaymentToken[]}
     */
    $scope.savedPaymentTokens = [];
    if ($scope.cart.currentCart.customer?.account?.ID) {
        apiClient
            .request(
                'GET',
                `customeraccounts/${$scope.cart.currentCart.customer.account.ID}/paymenttokens/active`
            )
            .then(({ data }) => {
                console.log('Payment tokens loaded', data);
                $scope.savedPaymentTokens = data
                    .map((token) => hydrate(token, new PaymentToken()))
                    .filter((token) => token.paymentMethodID === $scope.thisPaymentMethod.ID);
            })
            .catch((error) => {
                console.warn('Error loading saved payment tokens', error);
            })
            .finally(() => {
                updateAngularUi();
            });
    }

    /**
     * Display modal to start a transaction using an existing token.
     */
    $scope.showSavedCards = function showSavedCards() {
        if ($scope.transactionModifier === VerifoneTransactionModifiers.CNP_ACCOUNT_ON_FILE) {
            $scope.transactionModifier = null;
            $scope.accountOnFileToken = null;
            $scope.tryAgain();
            return;
        }

        $scope.privilegesManager
            .requirePrivilege(PrivilegeNames.CNP_PAYMENTS)
            .then(
                /**
                 * @param {PrivilegeCheckResult} privilegeCheckResult
                 */
                function (privilegeCheckResult) {
                    if (!privilegeCheckResult.granted) {
                        return;
                    }

                    vueModalFactory
                        .open(
                            MODAL_NAMES__EPOS.PAYMENT_TOKEN_SELECTION,
                            {
                                tokens: $scope.savedPaymentTokens,
                            }
                        )
                        .then((result) => {
                            if (result.token) {
                                // Card selected - switch to account on file transaction.
                                $scope.transactionModifier = VerifoneTransactionModifiers.CNP_ACCOUNT_ON_FILE;
                                $scope.accountOnFileToken = result.token;
                            } else {
                                // No card selected - cancel account on file.
                                $scope.transactionModifier = null;
                                $scope.accountOnFileToken = null;
                            }

                            updateAngularUi();

                            $scope.tryAgain();
                        })
                        .catch(suppressModalCancelledErrorOrReThrow);
                }
            );
    };

    $scope.tryAgain = function tryAgain() {
        if ($scope.verifoneDriver.state.isReady || !$scope.verifoneConfig.posClient) {
            $scope.beginTransaction($scope.cart.getRemainingBalance());
        } else {
            $scope.onReady = function () {
                $scope.beginTransaction($scope.cart.getRemainingBalance());
            };
            $scope.cancel();
        }
    };

    $scope.startManualReconciliation = () => {
        $scope.privilegesManager.requirePrivilege(PrivilegeNames.MANUAL_PAYMENT_RECONCILIATION).then((result) => {
            if (result.granted) {
                $scope.showManualReconciliation();
            }
        });
    };

    $scope.showManualReconciliation = () => {
        $scope.cancel();

        const amount = $scope.isRefund ? -$scope.transactionAmount : $scope.transactionAmount;
        // There must be no thousand separator used in the value in the input field.
        const moneyFormattedAmount = formatMoney(amount, 2, '.', '');
        const currencyFormattedAmount = formatCurrency(currencyService, amount);
        dialogService.prompt(
            '',
            {
                customHtml: `<p>A transaction of <strong>${currencyFormattedAmount}</strong> was started. If a technical issue has prevented this transaction completing normally, please manually check if the transaction was successful.<br/>To complete the transaction enter the auth code, which can be found on the receipt or in Webcom.</p>`,
                inputLabel: 'PAYMENT.AUTH_CODE',
                title: 'PAYMENT.MANUAL_RECONCILIATION_TITLE',
                okLabel: 'PAYMENT.MANUAL_RECONCILIATION_OK_BTN',
                okColor: 'success',
                cancelLabel: 'PAYMENT.MANUAL_RECONCILIATION_REJECT_BTN',
                cancelColor: 'danger',
                minLength: 2,
                maxLength: 9,
                keyUp: function (value) {
                    // Only letters and numbers in uppercase.
                    return value ? value.replace(/[^a-z0-9]/gi, '').toUpperCase().trim() : '';
                },
                customBtns: [
                    {
                        label: 'PAYMENT.REPRINT_BTN',
                        handler() {
                            verifoneDriver.reprintMerchantReceipt();
                            setTimeout(() => {
                                verifoneDriver.reprintCardholderReceipt();
                            }, 2000);
                        },
                    },
                ],
                customInputs: {
                    amount: {
                        value: moneyFormattedAmount,
                        label: 'PAYMENT.MANUAL_RECONCILIATION_AMOUNT',
                    },
                },
            }
        )
            .then((result) => {
                if (result.value) {
                    // Create a payment from the manual info.
                    const payment = new Payment();
                    payment.setTendered(result.amount);
                    payment.setAuthCode(result.value);
                    payment.setPaymentMethod($scope.thisPaymentMethod);
                    payment.setThirdPartyID($scope.reference);
                    $scope.addPaymentAndAutoFinish(payment);
                    setTransactionInProgress(false);
                }
            })
            .catch((error) => {
                if (error instanceof ModalCancelledError) {
                    $scope.transactionInProgress = false;
                    $scope.transactionAmount = null;
                } else {
                    throw error;
                }
            })
            .finally(() => updateAngularUi());
    };

    /**
     * @param {number} amount
     *
     * @return {VerifoneTransactionRequestRecord}
     */
    $scope.createTransactionRequestRecord = function createTransactionRequestRecord(amount) {
        $scope.transactionAmount = amount;
        $scope.clearCashbackAmount();
        $scope.cashbackAmount = null;
        let record = new VerifoneTransactionRequestRecord();

        record.reference = $scope.cart.generatePaymentThirdPartyID();
        $scope.reference = record.reference;

        if (amount < 0) {
            record.tranactionType = '02'; // Refund
            record.transactionValue = Math.abs(amount);
            $scope.isRefund = true;
        } else {
            record.transactionValue = amount;
            $scope.isRefund = false;
        }

        if ($scope.transactionModifier) {
            record.modifier = $scope.transactionModifier;

            if (record.modifier === VerifoneTransactionModifiers.CNP_ACCOUNT_ON_FILE) {
                // Add the selected token to the record.
                record.tokenId = $scope.accountOnFileToken.paymentMechanismReference;
            }
        }

        // Allow entry of gratuity if enabled by the company.
        record.suppressGratuityPrompt = userService.company.gratuityEnabled ? 0 : 1;

        // If a customer account is selected (but we are not making an account on file transaction) register for account on file.
        if ($scope.cart.customer.hasAccount() && $scope.transactionModifier !== VerifoneTransactionModifiers.CNP_ACCOUNT_ON_FILE) {
            record.registerForAccountOnFile = 2;
        } else {
            record.registerForAccountOnFile = 1;
        }

        return record;
    };

    /**
     * Begin a cardholder present transaction.
     *
     * @param {number} amount
     */
    $scope.transactionStarted = false;
    $scope.beginTransaction = function beginTransaction(amount) {
        // After an error it is possible to attempt a transaction with 0 amount so prevent this. Note at this point the
        // amount may be +ve or -ve as it gets handled by $scope.createTransactionRequestRecord() it just cannot be 0
        // because that will cause a payment terminal error.
        if (amount === 0) {
            return;
        }

        $scope.cancel();
        paymentState.setPaymentModalCancelable(false, $scope.thisPaymentMethodRef);
        $scope.verifoneDriver
            .reconnectProgressSocket()
            .then(() => {
                $scope.transactionStarted = true;
                $scope.lastTransactionStatusInfo = null;
                setTransactionInProgress(true);
                $scope.tryAgainIsVisible = false;
                $scope.addStatusInfo('Sending Transaction To Terminal', 'info', $scope.defaultWaitingIcon, false);

                let record = $scope.createTransactionRequestRecord(amount);
                $scope.verifoneDriver.sendRecord(record);

                // Show the 'Try Again' button after 3 seconds
                $timeout(
                    function () {
                        $scope.tryAgainIsVisible = true;
                    },
                    3000
                );
            })
            .catch(() => {
                setTransactionInProgress(false);
                paymentState.clearPaymentModalCancelable();
            });
    };

    /**
     * @param {string} text
     * @param {string} [textClass] success, danger, info
     * @param {string|boolean|null} [iconName]
     * @param {boolean} [isTransactionInfo] If true this will update the last transaction info instead of terminal info.
     * @param {boolean} [isHtml] Is the given text string HTML?
     */
    $scope.addStatusInfo = function addStatusInfo(text, textClass, iconName, isTransactionInfo, isHtml) {
        // Determine icon.
        let iconClass;
        if (iconName !== false) {
            if (iconName) {
                iconClass = 'fa-' + iconName;
            } else {
                switch (textClass) {
                    case 'danger':
                        iconClass = 'fa-exclamation-triangle';
                        break;

                    case 'success':
                        iconClass = 'fa-check-circle';
                        break;

                    case 'info':
                    default:
                        iconClass = 'fa-info-circle';
                        textClass = 'info'; // Set in case this was the default because an invalid class was given.
                        break;
                }
            }
        }

        let infoObject = {
            html: null,
            iconClass,
            text: null,
            textClass,
        };

        if (isHtml) {
            infoObject.html = text;
        } else {
            infoObject.text = toTitleCase(text);
        }

        if (isTransactionInfo) {
            $scope.lastTransactionStatusInfo = infoObject;
            if (!$scope.verifoneConfig.posClient) {
                // If this is not POS client we clear the terminal status, as the transaction status shows
                // the latest state instead.
                $scope.lastTerminalStatusInfo = null;
            }
        } else {
            $scope.lastTerminalStatusInfo = infoObject;
        }

        // Add to statusInfoHistory.
        // Prevent duplicating the last message.
        if ($scope.statusInfoHistory.length > 0 && $scope.statusInfoHistory[$scope.statusInfoHistory.length - 1].text === text) {
            $scope.statusInfoHistory[$scope.statusInfoHistory.length - 1].iconClass = iconClass;
            $scope.statusInfoHistory[$scope.statusInfoHistory.length - 1].textClass = textClass;
            return;
        }

        // Keep only the last 20 statuses.
        while ($scope.statusInfoHistory.length > 20) {
            $scope.statusInfoHistory.shift();
        }
    };

    /**
     * Verifone recommends displaying a more informative message for some statuses.
     *
     * @param {VerifoneStatusRecord} statusRecord
     *
     * @return {string} Returns a custom string or the original message if no custom string.
     */
    $scope.getStatusRecordDisplayMessage = function getStatusRecordDisplayMessage(statusRecord) {
        switch (statusRecord.statusName) {
            case VerifoneStatusNames.CONFIRM_AUTH_CODE:
                return statusRecord.message + ' (' + statusRecord.parameters['AUTH CODE'] + ')';

            case VerifoneStatusNames.EXPIRY_DATE_REQUIRED:
                return langService.getText('PAYMENT_VERIFONE.EXPIRY_DATE_REQUIRED');

            case VerifoneStatusNames.AVS_HOUSE_NUMBER_REQUIRED:
                return langService.getText('PAYMENT_VERIFONE.AVS_HOUSE_NUMBER_REQUIRED');

            case VerifoneStatusNames.AVS_POST_CODE_REQUIRED:
                return langService.getText('PAYMENT_VERIFONE.AVS_POST_CODE_REQUIRED');

            case VerifoneStatusNames.CSC_REQUIRED:
                return langService.getText('PAYMENT_VERIFONE.CSC_REQUIRED');
        }

        return statusRecord.message;
    };

    $scope.setLastIntegrationRecord = function setLastIntegrationRecord(error, info, success) {
        $scope.errorIntegrationRecord = error || null;
        $scope.infoIntegrationRecord = info || null;
        $scope.successIntegrationRecord = success || null;
    };

    $scope.$on('payment-modal.cancel-pressed', function (event, paymentMethodRef) {
        if (paymentMethodRef === $scope.thisPaymentMethodRef) {
            // Payment was cancelled from this tab.
            // Cancel payment on the terminal.
            $scope.cancel();
        }
    });

    $scope.$on(EventNames.PAYMENT_METHOD_CHANGED, function (event, newPaymentMethod) {
        const newPaymentMethodRef = newPaymentMethod?.ref;
        if (newPaymentMethodRef === $scope.thisPaymentMethodRef) {
            // Switching to this tab

            if ($scope.verifoneConfig.posClient) {
                // Engage terminals or VX820
                // Set the initial state when loading the payment method.
                // If the terminal is currently doing something (like downloading an update) we need to display a
                // message about that and wait for it to complete.
                $scope.initialReady = false;

                if ($scope.verifoneDriver.state.isReady) {
                    $scope.initialReady = true;
                    if (!$scope.transactionInProgress && $scope.autoStartCardTransactions) {
                        $scope.beginTransaction($scope.cart.getRemainingBalance());
                    }
                } else {
                    $scope.onReady = function () {
                        $scope.initialReady = true;
                        if ($scope.autoStartCardTransactions) {
                            $scope.beginTransaction($scope.cart.getRemainingBalance());
                        }
                    };
                }
            } else {
                // VX820 IP
                $scope.initialReady = true;
                if ($scope.autoStartCardTransactions) {
                    $scope.beginTransaction($scope.cart.getRemainingBalance());
                }
            }
        } else {
            // Switching away from this tab
            $scope.cancel();
        }
    });

    $scope.setCustomerFacingStatus = function setCustomerFacingStatus(status) {
        customerScreenDataService.updateState((state) => {
            state.stage = CustomerScreenStages.PAY;
            state.paymentStatus = status;
        });
    };
};

module.exports = VerifonePaymentCtrl;
